// Centralized API client with auto-refresh on 401
import type { ApiResponse } from '~/types';

const isDev = process.env.NODE_ENV === 'development';
const logWarn = (message: string, error?: unknown) => {
  if (isDev) console.warn(message, error);
};

// Module-scoped refresh lock  -  shared across all useApi() instances
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const useApi = () => {
  const config = useRuntimeConfig();
  const API = config.public.apiBaseUrl as string;

  const apiFetch = async <T = unknown>(
    path: string,
    opts: {
      method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
      body?: unknown;
      query?: Record<string, string | number | boolean | undefined>;
      headers?: Record<string, string>;
    } = {}
  ): Promise<ApiResponse<T>> => {
    const url = path.startsWith('http') ? path : `${API}${path}`;

    const userCookie = useCookie('a4t_admin_user');

    // Track whether the request got a 401
    let got401 = false;

    const doRequest = async (): Promise<ApiResponse<T>> => {
      try {
        return await $fetch<ApiResponse<T>>(url, {
          method: opts.method || 'GET',
          body: opts.body as BodyInit | null | undefined,
          query: opts.query,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(opts.headers || {}),
          },
        });
      } catch (err: unknown) {
        // $fetch throws on HTTP errors
        const fetchErr = err as { status?: number; statusCode?: number; response?: { status?: number }; data?: ApiResponse<T>; message?: string };
        const status = fetchErr?.status || fetchErr?.statusCode || fetchErr?.response?.status;

        // Flag 401 for refresh logic
        if (status === 401) {
          got401 = true;
          return {
            success: false,
            error: { code: 'UNAUTHORIZED', message: 'Unauthorized' },
          } as ApiResponse<T>;
        }

        // Return error response for other HTTP errors
        if (fetchErr?.data) {
          return fetchErr.data as ApiResponse<T>;
        }

        return {
          success: false,
          error: {
            code: 'NETWORK_ERROR',
            message: fetchErr?.message || 'Failed to connect to server',
          },
        } as ApiResponse<T>;
      }
    };

    const result = await doRequest();

    // Handle 401 - try refresh token (only once)
    if (got401) {
      // Prevent parallel refresh calls
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = doRefresh(API);
      }

      if (refreshPromise) {
        const ok = await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;

        if (ok) {
          // Retry with the rotated HttpOnly cookie
          got401 = false;
          return await doRequest();
        }
      }

      // Refresh failed  -  clear session and redirect
      userCookie.value = null;

      if (import.meta.client) {
        navigateTo('/login');
      }

      return {
        success: false,
        error: { code: 'UNAUTHORIZED', message: 'Session expired' },
      } as ApiResponse<T>;
    }

    return result;
  };

  return {
    get: <T = unknown>(path: string, query?: Record<string, string | number | boolean | undefined>) =>
      apiFetch<T>(path, { method: 'GET', query }),
    post: <T = unknown>(path: string, body?: unknown) =>
      apiFetch<T>(path, { method: 'POST', body }),
    patch: <T = unknown>(path: string, body?: unknown) =>
      apiFetch<T>(path, { method: 'PATCH', body }),
    put: <T = unknown>(path: string, body?: unknown) =>
      apiFetch<T>(path, { method: 'PUT', body }),
    delete: <T = unknown>(path: string) =>
      apiFetch<T>(path, { method: 'DELETE' }),
    apiFetch,
  };
};

// Internal refresh function (outside the composable to avoid reactivity issues)
async function doRefresh(API: string): Promise<boolean> {
  try {
    const res = await $fetch<ApiResponse<Record<string, never>>>(
      `${API}/auth/refresh`,
      {
        method: 'POST',
        body: {},
        credentials: 'include',
        headers: { 'X-Auth-Transport': 'cookie' },
      }
    );
    return res.success;
  } catch (err) {
    logWarn('[useApi] Token refresh failed:', err);
    return false;
  }
}
